<template>
    <div class="modal fade" id="modal-lot-transactions" tabindex="-1" aria-labelledby="modalLotTransactions"
        aria-hidden="true" ref="modal">
        <div class="modal-dialog modal-xl modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header bg-primary text-white">
                    <h1 class="modal-title fs-5 text-white">
                        <i class="fas fa-list-alt me-2"></i>รายละเอียด Stock Transactions
                    </h1>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"
                        aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <!-- Summary -->
                    <div v-if="currentLotInfo" class="alert alert-info mb-3">
                        <div class="row">
                            <div class="col-md-3">
                                <strong><i class="fas fa-barcode me-1"></i>เลขล็อต:</strong><br />
                                {{ currentLotInfo.manufacturing_no || '-' }}
                            </div>
                            <div class="col-md-3">
                                <strong><i class="far fa-calendar-alt me-1"></i>วันหมดอายุ:</strong><br />
                                {{
                                    currentLotInfo.expiry_date
                                        ? format_thai_date(currentLotInfo.expiry_date)
                                        : '-'
                                }}
                            </div>
                            <div class="col-md-3">
                                <strong><i class="fas fa-list me-1"></i>จำนวน Transactions:</strong><br />
                                {{ lotTransactions.length + newTransactionRows.length }} รายการ
                            </div>
                            <div class="col-md-3">
                                <strong><i class="fas fa-boxes me-1"></i>รวมคงเหลือ:</strong><br />
                                <span :class="getQuantityStatusClass()">
                                    {{ formatNumber(totalRemainingAmount) }} หน่วย
                                </span>
                            </div>
                            <div class="col-md-3">
                                <strong><i class="fas fa-bullseye me-1"></i>จำนวนเป้าหมาย:</strong><br />
                                <span class="text-primary">
                                    {{ formatNumber(targetQuantity) }} หน่วย
                                </span>
                            </div>
                        </div>
                        <!-- Quantity Validation Alert -->
                        <div v-if="shouldShowQuantityWarning" class="alert alert-danger mt-2 mb-0">
                            <i class="fas fa-exclamation-triangle me-2"></i>
                            <strong>คำเตือน:</strong> จำนวนรวมคงเหลือ ({{ formatNumber(totalRemainingAmount) }})
                            ไม่ตรงกับจำนวนเป้าหมาย ({{ formatNumber(targetQuantity) }})
                            <span v-if="quantityDifference > 0" class="ms-2">
                                (เกิน {{ formatNumber(quantityDifference) }} หน่วย)
                            </span>
                            <span v-else-if="quantityDifference < 0" class="ms-2">
                                (ขาด {{ formatNumber(Math.abs(quantityDifference)) }} หน่วย)
                            </span>
                        </div>
                    </div>

                    <!-- Transactions Table -->
                    <div class="table-responsive" style="max-height: 500px; overflow-y: auto">
                        <table class="table table-bordered table-striped table-hover">
                            <thead class="sticky-top bg-white">
                                <tr>
                                    <th style="width: 120px">เลขล็อต</th>
                                    <th style="width: 120px">วันหมดอายุ</th>
                                    <th style="width: 200px">ตำแหน่งคลัง</th>
                                    <th style="width: 120px">จำนวนคงเหลือ</th>
                                    <th style="width: 120px">จำนวนนำเข้า</th>
                                    <th>หมายเหตุ</th>
                                    <th style="width: 80px">สถานะ</th>
                                    <th style="width: 80px">จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loading">
                                    <td colspan="8" class="text-center py-5">
                                        <div class="spinner-border text-primary" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        <p class="mt-2 mb-0 text-muted">กำลังโหลดข้อมูล...</p>
                                    </td>
                                </tr>
                                <tr v-else-if="
                                    lotTransactions.length === 0 &&
                                    newTransactionRows.length === 0
                                ">
                                    <td colspan="8" class="text-center py-5 text-muted">
                                        <i class="fas fa-inbox fa-3x mb-3 d-block"></i>
                                        ไม่พบข้อมูล Stock Transactions
                                    </td>
                                </tr>
                                <!-- Existing Transactions -->
                                <tr v-for="(transaction, i) in lotTransactions" :key="transaction.id">
                                    <td>{{ transaction.manufacturing_no }}</td>
                                    <td>{{ transaction.expiry_date }}</td>
                                    <td>
                                        <input type="number" class="form-control form-control-sm text-end"
                                            v-model.number="transaction.remaining_amount"
                                            :max="transaction.change_amount" min="0" step="1"
                                            @input="$emit('validate-remaining', transaction)" />
                                    </td>
                                    <td class="text-end">
                                        {{ formatNumber(transaction.change_amount) }}
                                    </td>
                                    <td>
                                        <div style="width: 200px;">
                                            <tree-select
                                                :key="`shelf-${transaction.id}-${transaction.shelf_level_id || 'null'}`"
                                                :options="warehouses" :value="transaction.shelf_level_id"
                                                v-on:tree-select="(value) => selectLocation(transaction, value, i)"
                                                v-on:de-select="() => transaction.shelf_level_id = null"
                                                :ref="`shelf_level_id_${i}`" />
                                        </div>
                                    </td>
                                    <td>{{ transaction.description || '-' }}</td>
                                    <td>
                                        <span class="badge bg-info">ต้นฉบับ</span>
                                    </td>
                                    <td></td>
                                </tr>
                                <!-- New Transaction Rows -->
                                <tr v-for="(newRow, index) in newTransactionRows" :key="'new-' + index"
                                    class="table-success">
                                    <td>{{ newRow.manufacturing_no }}</td>
                                    <td>{{ newRow.expiry_date }}</td>
                                    <td>
                                        <input type="number" class="form-control form-control-sm text-end"
                                            v-model.number="newRow.remaining_amount" min="0" step="1"
                                            placeholder="กรอกจำนวน" />
                                    </td>
                                    <td class="text-end">
                                        <strong>{{ formatNumber(newRow.remaining_amount) }}</strong>
                                    </td>
                                    <td>
                                        <tree-select :key="`shelf-new-${index}-${newRow.shelf_level_id || 'null'}`"
                                            :options="warehouses" :value="newRow.shelf_level_id"
                                            v-on:tree-select="(value) => selectLocation(newRow, value, index)"
                                            v-on:de-select="() => newRow.shelf_level_id = null"
                                            :ref="`shelf_level_id_new_${index}`" />
                                    </td>
                                    <td>
                                        <input type=" text" class="form-control form-control-sm"
                                            v-model="newRow.description" placeholder="หมายเหตุ (ถ้ามี)" />
                                    </td>
                                    <td>
                                        <span class="badge bg-warning">Manual</span>
                                    </td>
                                    <td>
                                        <button type="button" class="btn btn-danger btn-sm"
                                            @click="$emit('remove-transaction', index)" title="ลบ">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot v-if="lotTransactions.length > 0 || newTransactionRows.length > 0"
                                class="sticky-bottom bg-light">
                                <tr>
                                    <td colspan="3" class="text-end"><strong>รวม:</strong></td>
                                    <td class="text-end">
                                        <strong>{{ formatNumber(totalRemainingAmount) }}</strong>
                                    </td>
                                    <td class="text-end">
                                        <strong>{{ formatNumber(totalChangeAmount) }}</strong>
                                    </td>
                                    <td colspan="3" class="text-start">
                                        <small class="text-muted">
                                            รายการต้นฉบับ: {{ lotTransactions.length }} | รายการใหม่:
                                            {{ newTransactionRows.length }}
                                        </small>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <div class="modal-footer">
                    <div class="w-100 d-flex justify-content-between align-items-center">
                        <div>
                            <button type="button" class="btn btn-success" @click="$emit('add-transaction')">
                                <i class="fas fa-plus me-1"></i>เพิ่มรายการ
                            </button>
                        </div>
                        <div class="ms-auto">
                            <button type="button" class="btn btn-primary" @click="confirmTransaction"
                                :disabled="!isQuantityMatching" :class="{ 'disabled': !isQuantityMatching }">
                                <i class="fas fa-check me-1"></i>ยืนยันการจัดการ
                            </button>
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                                <i class="fas fa-times me-1"></i>ปิด
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from '/assets/js/plugins/sweetalert2/sweetalert2.min.js';

export default {
    name: 'StockCountLotTransactionsModal',

    props: {
        currentLotInfo: {
            type: Object,
            default: null,
        },
        lotTransactions: {
            type: Array,
            default: () => [],
        },
        newTransactionRows: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        targetQuantity: {
            type: Number,
            default: 0,
        },
        warehouses: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            modal: null,
        };
    },

    computed: {
        totalRemainingAmount() {
            const existingTotal = this.lotTransactions.reduce(
                (sum, t) => sum + Number(t.remaining_amount || 0),
                0
            );
            const newTotal = this.newTransactionRows.reduce(
                (sum, t) => sum + Number(t.remaining_amount || 0),
                0
            );
            return existingTotal + newTotal;
        },

        totalChangeAmount() {
            const existingTotal = this.lotTransactions.reduce(
                (sum, t) => sum + Number(t.change_amount || 0),
                0
            );
            const newTotal = this.newTransactionRows.reduce(
                (sum, t) => sum + Number(t.change_amount || 0),
                0
            );
            return existingTotal + newTotal;
        },

        isQuantityMatching() {
            // ถ้ายังไม่มี transaction เลย ให้ถือว่าตรง (lot ใหม่ที่ยังไม่ได้จัดการ)
            if (this.lotTransactions.length === 0 && this.newTransactionRows.length === 0) {
                return true;
            }
            // ถ้ามี transaction แล้ว ต้องเช็คว่าจำนวนตรงกับเป้าหมาย
            return parseInt(this.totalRemainingAmount) === parseInt(this.targetQuantity);
        },

        quantityDifference() {
            return this.totalRemainingAmount - this.targetQuantity;
        },

        shouldShowQuantityWarning() {
            // ไม่แสดง warning หากยังไม่มี transaction เลย (lot ใหม่)
            if (this.lotTransactions.length === 0 && this.newTransactionRows.length === 0) {
                return false;
            }

            // แสดง warning หากมี transaction แล้วและจำนวนไม่ตรง
            return !this.isQuantityMatching;
        },
    },

    mounted() {
        this.modal = new bootstrap.Modal(this.$refs.modal, {});
    },

    methods: {
        show() {
            this.modal.show();
        },

        hide() {
            this.modal.hide();
        },

        formatNumber(n) {
            return new Intl.NumberFormat('us-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(n);
        },

        format_thai_date(date) {
            return window.format_thai_date(date);
        },

        getQuantityStatusClass() {
            // ถ้ายังไม่มี transaction เลย ไม่ต้องแสดงสี (lot ใหม่)
            if (this.lotTransactions.length === 0 && this.newTransactionRows.length === 0) {
                return '';
            }

            if (!this.targetQuantity) return '';

            if (this.totalRemainingAmount === this.targetQuantity) {
                return 'text-success fw-bold';
            } else if (this.totalRemainingAmount > this.targetQuantity) {
                return 'text-danger fw-bold';
            } else {
                return 'text-warning fw-bold';
            }
        },

        selectLocation(transaction, value, index) {

            if (value.level != 4) {
                Swal.fire({
                    title: 'ไม่สามารถเลือกคลังสินค้าได้',
                    text: 'ไม่สามารถเลือกคลังสินค้าได้ กรุณาสร้าง (ชั้นของเชลฟ์) ก่อน',
                    icon: 'error',
                });
                if (transaction.is_new) {
                    this.$refs[`shelf_level_id_new_${index}`][0].selected_value = null;
                } else {
                    this.$refs[`shelf_level_id_${index}`][0].selected_value = null;
                }

                // Use Vue.set to trigger reactivity and re-render with :key binding
                this.$set(transaction, 'shelf_level_id', null);
                return;
            }

            // Use Vue.set to trigger reactivity and re-render with :key binding
            this.$set(transaction, 'shelf_level_id', value.id);
        },

        confirmTransaction() {
            // ถ้ายังไม่มี transaction เลย ให้บันทึกได้เลย (lot ใหม่)
            if (this.lotTransactions.length === 0 && this.newTransactionRows.length === 0) {
                this.$emit('confirm');
                return;
            }

            // ถ้ามี transaction แล้ว ต้องเช็คว่าจำนวนตรงกับเป้าหมาย
            if (!this.isQuantityMatching) {
                Swal.fire({
                    title: 'ไม่สามารถบันทึกได้',
                    html: `
                        <p>จำนวนรวมคงเหลือไม่ตรงกับจำนวนเป้าหมาย</p>
                        <div class="alert alert-danger mt-3">
                            <strong>จำนวนรวมคงเหลือ:</strong> ${this.formatNumber(this.totalRemainingAmount)} หน่วย<br>
                            <strong>จำนวนเป้าหมาย:</strong> ${this.formatNumber(this.targetQuantity)} หน่วย<br>
                            <strong>ส่วนต่าง:</strong> ${this.quantityDifference > 0 ? 'เกิน' : 'ขาด'} ${this.formatNumber(Math.abs(this.quantityDifference))} หน่วย
                        </div>
                        <p class="mt-2">กรุณาแก้ไขจำนวนให้ตรงกับเป้าหมายก่อนบันทึก</p>
                    `,
                    icon: 'error',
                    confirmButtonText: 'ตกลง',
                });
                return;
            }

            this.$emit('confirm');
        },
    },
};
</script>
